import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { getSuspendedUsers } from "../../services/UserService";
import { scrollToPageTop } from "../../utils/scrollToPageTop";

const SUSPENDED_ACCENT = '#c99509';
const SUSPENDED_TINT   = 'rgba(255, 193, 7, 0.15)';

const SuspendedIcon = ({ size = 32 }) => (
    <span
        className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
        style={{
            width: size,
            height: size,
            background: SUSPENDED_TINT,
            color: SUSPENDED_ACCENT,
        }}
        aria-hidden="true"
    >
        <i className="bi bi-slash-circle" style={{ fontSize: size * 0.55 }} />
    </span>
);

const RoleBadge = ({ role }) => (
    <span
        className="badge badge-rounded"
        style={{
            background: SUSPENDED_TINT,
            color: SUSPENDED_ACCENT,
            fontSize: '0.7rem',
            fontWeight: 600,
        }}
    >
        {role}
    </span>
);

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null;
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    );
};

const SuspendedUsers = () => {
    const partnerId = useSelector(state => state.auth.authUser?.partner_id);
    const [searchInput, setSearchInput]   = useState('');
    const [searchTerm, setSearchTerm]     = useState('');
    const [currentPage, setCurrentPage]   = useState(1);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchTerm(searchInput);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const { data, isPending, isError, isFetching } = useQuery({
        queryKey: ['suspended-users', partnerId, currentPage, searchTerm],
        queryFn:  () => getSuspendedUsers(partnerId, currentPage, searchTerm),
        enabled:  !!partnerId,
        staleTime: 0,
    });

    const suspensions = data?.data?.data ?? [];
    const meta        = data?.data?.meta ?? {};
    const totalPages  = meta.last_page ?? 1;

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                            <h4 className="card-title mb-0">Suspended Users</h4>
                            <div className="ms-sm-auto w-100" style={{ maxWidth: '320px' }}>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search by name, email or reason…"
                                    value={searchInput}
                                    onChange={e => setSearchInput(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="card-body p-0">
                            {isPending ? (
                                <div className="d-flex justify-content-center py-4">
                                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                </div>
                            ) : isError ? (
                                <p className="text-center text-danger py-4 mb-0">
                                    Failed to load suspended users. Please try again.
                                </p>
                            ) : suspensions.length === 0 ? (
                                <p className="text-center text-muted py-4 mb-0">
                                    {searchTerm
                                        ? 'No suspended users match your search.'
                                        : 'No suspended users.'}
                                </p>
                            ) : (
                                <>
                                    <div className="table-responsive d-none d-xl-block px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Name</th>
                                                    <th scope="col">Email</th>
                                                    <th scope="col">Role</th>
                                                    <th scope="col">Reason</th>
                                                    <th scope="col">Start Date</th>
                                                    <th scope="col">End Date</th>
                                                    <th scope="col">Suspended By</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {suspensions.map((suspension) => (
                                                    <tr key={suspension.id}>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <SuspendedIcon size={32} />
                                                                <span className="fw-semibold" style={{ overflowWrap: 'anywhere' }}>
                                                                    {suspension.user?.full_name ?? '-'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td style={{ overflowWrap: 'anywhere' }}>{suspension.user?.email ?? '-'}</td>
                                                        <td>{suspension.user?.role ? <RoleBadge role={suspension.user.role} /> : '-'}</td>
                                                        <td style={{ maxWidth: '240px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {suspension.suspension_reason ?? '-'}
                                                        </td>
                                                        <td className="text-nowrap">{suspension.suspension_start_date ?? '-'}</td>
                                                        <td className="text-nowrap">{suspension.suspension_end_date ?? '-'}</td>
                                                        <td>{suspension.suspended_by_name ?? '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">User</th>
                                                    <th scope="col">Suspended by</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {suspensions.map((suspension) => (
                                                    <tr key={suspension.id}>
                                                        <td>
                                                            <div className="d-flex align-items-start gap-2">
                                                                <SuspendedIcon size={32} />
                                                                <div style={{ minWidth: 0 }}>
                                                                    <div className="fw-semibold" style={{ overflowWrap: 'anywhere' }}>
                                                                        {suspension.user?.full_name ?? '-'}
                                                                    </div>
                                                                    {suspension.user?.role && (
                                                                        <div className="mt-1"><RoleBadge role={suspension.user.role} /></div>
                                                                    )}
                                                                    <div className="small text-muted mt-1" style={{ overflowWrap: 'anywhere' }}>
                                                                        {suspension.user?.email ?? '-'}
                                                                    </div>
                                                                    {suspension.suspension_reason && (
                                                                        <div className="mt-2" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            {suspension.suspension_reason}
                                                                        </div>
                                                                    )}
                                                                    <div className="small text-muted mt-2">
                                                                        <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                                        {suspension.suspension_start_date ?? '-'} to {suspension.suspension_end_date ?? '-'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>{suspension.suspended_by_name ?? '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <ul className="list-group list-group-flush d-block d-md-none">
                                        {suspensions.map((suspension) => (
                                            <li
                                                key={suspension.id}
                                                className="list-group-item py-3"
                                                style={{ borderLeft: `3px solid ${SUSPENDED_ACCENT}` }}
                                            >
                                                <div className="d-flex gap-3">
                                                    <SuspendedIcon size={40} />
                                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                        <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                                                            <span className="fw-semibold text-body" style={{ overflowWrap: 'anywhere' }}>
                                                                {suspension.user?.full_name ?? '-'}
                                                            </span>
                                                            {suspension.user?.role && <RoleBadge role={suspension.user.role} />}
                                                        </div>
                                                        <div className="small text-muted mb-1" style={{ overflowWrap: 'anywhere' }}>
                                                            {suspension.user?.email ?? '-'}
                                                        </div>
                                                        {suspension.suspension_reason && (
                                                            <div className="mb-1" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {suspension.suspension_reason}
                                                            </div>
                                                        )}
                                                        <div className="small text-muted">
                                                            <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                            {suspension.suspension_start_date ?? '-'} to {suspension.suspension_end_date ?? '-'}
                                                        </div>
                                                        <div className="small text-muted">
                                                            <i className="bi bi-person me-1" aria-hidden="true" />
                                                            {suspension.suspended_by_name ?? '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                            <PaginationBar
                                page={currentPage}
                                lastPage={totalPages}
                                isLoading={isFetching}
                                onPrev={() => { setCurrentPage(p => Math.max(p - 1, 1)); scrollToPageTop(); }}
                                onNext={() => { setCurrentPage(p => Math.min(p + 1, totalPages)); scrollToPageTop(); }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default SuspendedUsers;
